// Supplementary Self-vs-Self reach estimate for Flex findings — layered on
// top of the core angle/ratio magnitudes in pipeline.ts, not a replacement.
// See docs/FLEX_WELLNESS_ABGRENZUNG.md §3a: showing this alongside the Ampel
// band is a deliberate 02.07.2026 Founder decision, never worded as a
// clinical finding.
//
// Deliberately percent-of-leg-length, NOT height-calibrated cm: a cm value
// needs the user's height on file and inherits gait/calibration.ts's own
// disclosed ±10–15% height-based error on top of the pose-estimation error —
// two stacked estimates for a "precise-looking" absolute number. Percent of
// the person's own leg length in the SAME clip needs no extra input, no
// calibration step, and is self-normalizing (works the same for a 1.60 m and
// a 1.95 m person), at the cost of being a relative rather than absolute
// number. Given Flex is a Self-vs-Self tool (not a clinical goniometer
// replacement either way), that trade-off is the right one here.

import { Joints, type PoseFrame, type Side } from "../pose/landmarks";
import { isoDist } from "../gait/calibration";
import { percentile } from "../gait/geometry";

/**
 * Estimated "floor reach" of a forward bend as % of the person's own leg
 * length (hip→ankle): 100% ≈ fingers reached heel height, 0% ≈ fingers
 * stayed a full leg-length above the heel. A rough proxy for the classic
 * "finger-to-floor" test, expressed relatively instead of in cm on purpose
 * (see module comment above).
 *
 * Uses wrist landmarks as a fingertip proxy (MediaPipe's pose model has no
 * fingertip point) and heel height as a floor proxy. Returns null when too
 * few usable frames are present.
 */
export function estimateFloorReachPercent(frames: PoseFrame[], aspect: number): number | null {
  const gapsAboveHeel: number[] = [];
  const legLens: number[] = [];
  for (const f of frames) {
    const j = new Joints(f);
    for (const side of ["L", "R"] as Side[]) {
      const wrist = j.wrist(side);
      const heel = j.heel(side);
      const hip = j.hip(side);
      const ankle = j.ankle(side);
      if (Math.min(wrist.visibility, heel.visibility) >= 0.5) {
        // Normalized y grows downward (0 = top of frame, 1 = bottom), so
        // "wrist above heel" (not bent) means wrist.y < heel.y. This value
        // is large and positive when standing straight, and shrinks toward
        // 0 as the wrist descends toward heel/floor level during the bend.
        gapsAboveHeel.push(heel.y - wrist.y);
      }
      if (Math.min(hip.visibility, ankle.visibility) >= 0.5) {
        legLens.push(isoDist(hip, ankle, aspect));
      }
    }
  }
  if (gapsAboveHeel.length < 3 || legLens.length < 3) return null;

  // Deepest point of the bend = smallest gap-above-heel across the clip.
  const deepestGap = percentile(gapsAboveHeel, 5);
  const legLen = percentile(legLens, 50);
  if (legLen <= 1e-4) return null;

  const percent = 100 - (deepestGap / legLen) * 100;
  return Math.min(100, Math.max(0, Math.round(percent)));
}
